import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Legacy clipboard copy via a hidden textarea + execCommand. Works inside a
 * cross-origin iframe that hasn't been granted the `clipboard-write` Permissions
 * Policy (where `navigator.clipboard` is blocked), as long as it runs in a user
 * gesture. Best-effort: returns whether the copy succeeded.
 */
function legacyCopy(text: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    // Keep it out of view and avoid scroll jumps.
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Copies text to the system clipboard and exposes a short-lived `copied` flag
 * (auto-resets after `resetMs`) so callers can swap to a "copied" affordance.
 * Prefers the async Clipboard API and falls back to a legacy `execCommand` copy
 * when it's unavailable or blocked (e.g. an iframe without `clipboard-write`).
 * Returns `false` only if both approaches fail.
 */
export function useCopyToClipboard(resetMs = 2000) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const markCopied = useCallback(() => {
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), resetMs);
  }, [resetMs]);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          markCopied();
          return true;
        }
      } catch {
        // Fall through to the legacy path (Clipboard API blocked/denied).
      }

      if (legacyCopy(text)) {
        markCopied();
        return true;
      }

      setCopied(false);
      return false;
    },
    [markCopied],
  );

  return { copied, copy };
}
